import { supabase } from '@/lib/supabase';

/**
 * Serviço para atualização automática diária do cache de categorias
 * Este serviço pode ser executado por um cron job ou função programada
 */

interface CacheData {
  categorias: any[];
  subcategorias: any[];
  servicos: any[];
  lastUpdated: string;
}

const CACHE_KEY = 'categories-cache';
const SUBSCRIPTION_CACHE_KEY = 'subscription-cache';

class CacheUpdateService {
  
  /**
   * Atualiza o cache de categorias, subcategorias e serviços
   * @returns Promise<boolean> - true se a atualização foi bem-sucedida
   */
  async updateCache(): Promise<boolean> {
    try {
      console.log('🔄 Iniciando atualização automática do cache...');
      
      // Buscar dados atualizados do Supabase usando cliente JWT
      const [categoriasResult, subcategoriasResult, servicosResult, plansResult, methodsResult] = await Promise.allSettled([
        supabase.from('categorias').select('*').eq('ativo', true).order('ordem', { ascending: true }),
        supabase.from('subcategorias').select('*').eq('ativo', true).order('ordem', { ascending: true }),
        supabase.from('servicos').select('*').eq('ativo', true).order('titulo', { ascending: true }),
        supabase.from('subscription_plans').select('*').eq('is_active', true).order('price', { ascending: true }),
        supabase.from('subscription_payment_methods').select('*').eq('is_available', true).order('priority_order', { ascending: true })
      ]);

      const newCacheData: CacheData = {
        categorias: categoriasResult.status === 'fulfilled' && categoriasResult.value.data 
          ? categoriasResult.value.data 
          : [],
        subcategorias: subcategoriasResult.status === 'fulfilled' && subcategoriasResult.value.data 
          ? subcategoriasResult.value.data 
          : [],
        servicos: servicosResult.status === 'fulfilled' && servicosResult.value.data 
          ? servicosResult.value.data 
          : [],
        lastUpdated: new Date().toISOString()
      };

      // Salvar no localStorage
      localStorage.setItem(CACHE_KEY, JSON.stringify(newCacheData));

      // Também atualizar cache de subscrição
      const subscriptionCacheData = {
        plans: plansResult.status === 'fulfilled' && plansResult.value.data ? plansResult.value.data : [],
        paymentMethods: methodsResult.status === 'fulfilled' && methodsResult.value.data ? methodsResult.value.data : [],
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem(SUBSCRIPTION_CACHE_KEY, JSON.stringify(subscriptionCacheData));
      
      console.log('✅ Cache atualizado com sucesso:', {
        categorias: newCacheData.categorias.length,
        subcategorias: newCacheData.subcategorias.length,
        servicos: newCacheData.servicos.length,
        plans: subscriptionCacheData.plans.length,
        paymentMethods: subscriptionCacheData.paymentMethods.length,
        timestamp: newCacheData.lastUpdated
      });

      return true;
    } catch (error) {
      console.error('❌ Erro ao atualizar cache:', error);
      return false;
    }
  }

  /**
   * Verifica se o cache precisa ser atualizado (mais de 24h)
   * @returns boolean
   */
  needsUpdate(): boolean {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return true;

      const cacheData = JSON.parse(cached) as CacheData;
      const now = new Date().getTime();
      const cacheTime = new Date(cacheData.lastUpdated).getTime();
      const hoursSinceUpdate = (now - cacheTime) / (1000 * 60 * 60);

      return hoursSinceUpdate >= 24;
    } catch {
      return true;
    }
  }

  /**
   * Inicializa o serviço de atualização automática
   * Verifica se precisa atualizar e agenda próximas verificações
   */
  async initialize(): Promise<void> {
    if (import.meta.env.DEV) console.log('🚀 Inicializando serviço de cache...');
    
    // Verificar se precisa atualizar imediatamente
    if (this.needsUpdate()) {
      await this.updateCache();
    }

    // Agendar verificação a cada hora
    setInterval(() => {
      if (this.needsUpdate()) {
        this.updateCache();
      }
    }, 60 * 60 * 1000); // 1 hora

    if (import.meta.env.DEV) console.log('✅ Serviço de cache inicializado');
  }

  /**
   * Força a atualização do cache (para uso manual)
   */
  async forceUpdate(): Promise<boolean> {
    console.log('🔄 Forçando atualização do cache...');
    return await this.updateCache();
  }

  /**
   * Limpa o cache (para desenvolvimento/debug)
   */
  clearCache(): void {
    localStorage.removeItem(CACHE_KEY);
    console.log('🗑️ Cache limpo');
  }

  /**
   * Retorna informações sobre o status do cache
   */
  getCacheInfo(): { hasCache: boolean; lastUpdated?: string; needsUpdate: boolean } {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) {
        return { hasCache: false, needsUpdate: true };
      }

      const cacheData = JSON.parse(cached) as CacheData;
      return {
        hasCache: true,
        lastUpdated: cacheData.lastUpdated,
        needsUpdate: this.needsUpdate()
      };
    } catch {
      return { hasCache: false, needsUpdate: true };
    }
  }
}

// Instância singleton
export const cacheUpdateService = new CacheUpdateService();

// Auto-inicializar o serviço quando o módulo é importado
if (typeof window !== 'undefined') {
  // Aguardar um pouco para não bloquear o carregamento inicial
  setTimeout(() => {
    cacheUpdateService.initialize();
  }, 2000);
}

export default cacheUpdateService;